import axios, { AxiosError, AxiosResponse } from "axios";
import { CachedItem } from "@lib-shared/lib";
import { appSettings } from "core/config";
import { cloneObject } from "core/lib";

const isEmpty = (o: any) => !!o && !Object.keys(o).length

const _clean = (o: any) => {
    delete o?.transformRequest;
    delete o?.transformResponse;
    delete o?.maxContentLength;
    delete o?.signal;
    delete o?.xsrfCookieName;
    delete o?.xsrfHeaderName;
    delete o?.timeout;
    delete o?.statusText;

    if (o?.data === null) {
        delete o?.data;
    }

    if (isEmpty(o?.request)) {
        delete o.request;
    }

    if (isEmpty(o?.params)) {
        delete o.params;
    }
}

const isJson = (data: string) => {
    return data && data.startsWith("{") && data.endsWith("}");
}

const parseDataIfJson = (o: any) => {
    const data = o?.data?.toString() ?? "";

    if (!isJson(data)) {
        return;
    }

    o.data = JSON.parse(data);
}

class AxiosInterceptorLogger {
    logs: Array<any> = [];
    private _initialized = false;
    private _cache: CachedItem | undefined;

    constructor(cacheName: string | undefined) {
        if (!cacheName) {
            return;
        }

        this._cache = new CachedItem(cacheName, true, 0, false);
    }

    get isInitialized() {
        return this._initialized;
    }

    protected interceptResponse(response: AxiosResponse<any>) {
        const clone = cloneObject(response);
        clone.date = new Date();

        _clean(clone);
        _clean(clone?.config);

        parseDataIfJson(clone?.config);

        this.log(clone);
    }

    protected interceptError(error: any) {
        const axiosError = error as AxiosError;
        const isError = axiosError?.isAxiosError ?? false;

        if (!isError) {
            error.date = new Date();
            this.log(error);
            return;
        }

        const clone = cloneObject(axiosError);
        clone.date = new Date();
        clone.isAxiosError = true;
        clone.response = cloneObject(axiosError.response);
        delete clone?.response?.request;

        _clean(clone?.config);
        _clean(clone.response);

        parseDataIfJson(clone?.response?.config);

        delete clone?.response?.config;

        this.log(clone);
    }

    initialize() {
        if (this._initialized) {
            return;
        }

        //Only need to log response as the response  features the request
        //TODO: Ignore certain responses - e.g. downloads & big responses such as activity logs
        //Potentially could ignore based on a header that can be removed if it interferes with CORS
        axios.interceptors.response.use((response) => {
            if (appSettings.isEnabled.logAllResponsesToDatabase) {
                this.interceptResponse(response);
            }

            return response;
        }, (error) => {
            if (appSettings.isEnabled.logErrorsToDatabase) {
                this.interceptError(error);
            }

            return Promise.reject(error);
        });

        this._initialized = true;

        this.logs = this._cache?.value ?? [];
    }

    log(o: any) {
        this.logs.push(o);
        this._cache?.set(this.logs);
    }

    clear() {
        this.logs = [];
        this._cache?.clear();
    }

    getLogs(callback: any) {
        callback(this.logs);
    }
}

export default AxiosInterceptorLogger;
